import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface PlotPosition {
  x: number;
  y: number;
}

interface PlotSize {
  width: number;
  height: number;
}

interface PlotLayoutState {
  viewMode: 'grid' | 'canvas';
  gridConfig: {
    rows: number;
    cols: number;
  };
  gridAssignments: Record<string, number>; // plotId -> cellIndex
  canvasPositions: Record<string, PlotPosition>;
  plotSizes: Record<string, PlotSize>;
}

interface PlotViewStore {
  tabLayouts: Record<string, PlotLayoutState>;

  // Actions
  setViewMode: (tabId: string, mode: 'grid' | 'canvas') => void;
  setGridConfig: (tabId: string, rows: number, cols: number) => void;
  setGridAssignment: (tabId: string, plotId: string, cellIndex: number) => void;
  setCanvasPosition: (tabId: string, plotId: string, position: PlotPosition) => void;
  setPlotSize: (tabId: string, plotId: string, size: PlotSize) => void;
  getTabLayout: (tabId: string) => PlotLayoutState;
  initializeTabLayout: (tabId: string, plotIds: string[]) => void;
  clearTabLayout: (tabId: string) => void;
}

const DEFAULT_LAYOUT: PlotLayoutState = {
  viewMode: 'grid',
  gridConfig: { rows: 2, cols: 2 },
  gridAssignments: {},
  canvasPositions: {},
  plotSizes: {},
};

const DEFAULT_PLOT_SIZE: PlotSize = { width: 450, height: 350 };

export const usePlotViewStore = create<PlotViewStore>()(
  persist(
    (set, get) => ({
      tabLayouts: {},

      setViewMode: (tabId, mode) => {
        set((state) => ({
          tabLayouts: {
            ...state.tabLayouts,
            [tabId]: {
              ...get().getTabLayout(tabId),
              viewMode: mode,
            },
          },
        }));
      },

      setGridConfig: (tabId, rows, cols) => {
        set((state) => ({
          tabLayouts: {
            ...state.tabLayouts,
            [tabId]: {
              ...get().getTabLayout(tabId),
              gridConfig: { rows, cols },
            },
          },
        }));
      },

      setGridAssignment: (tabId, plotId, cellIndex) => {
        set((state) => {
          const layout = get().getTabLayout(tabId);
          return {
            tabLayouts: {
              ...state.tabLayouts,
              [tabId]: {
                ...layout,
                gridAssignments: {
                  ...layout.gridAssignments,
                  [plotId]: cellIndex,
                },
              },
            },
          };
        });
      },

      setCanvasPosition: (tabId, plotId, position) => {
        set((state) => {
          const layout = get().getTabLayout(tabId);
          return {
            tabLayouts: {
              ...state.tabLayouts,
              [tabId]: {
                ...layout,
                canvasPositions: {
                  ...layout.canvasPositions,
                  [plotId]: position,
                },
              },
            },
          };
        });
      },

      setPlotSize: (tabId, plotId, size) => {
        set((state) => {
          const layout = get().getTabLayout(tabId);
          return {
            tabLayouts: {
              ...state.tabLayouts,
              [tabId]: {
                ...layout,
                plotSizes: {
                  ...layout.plotSizes,
                  [plotId]: size,
                },
              },
            },
          };
        });
      },

      getTabLayout: (tabId) => {
        const state = get();
        return state.tabLayouts[tabId] || { ...DEFAULT_LAYOUT };
      },

      initializeTabLayout: (tabId, plotIds) => {
        const existingLayout = get().tabLayouts[tabId];

        // If layout exists and has assignments, don't reinitialize
        if (existingLayout && Object.keys(existingLayout.gridAssignments).length > 0) {
          // Just ensure all plotIds have assignments
          const newAssignments = { ...existingLayout.gridAssignments };
          const newPositions = { ...existingLayout.canvasPositions };
          const newSizes = { ...existingLayout.plotSizes };

          let nextCellIndex = Math.max(-1, ...Object.values(newAssignments)) + 1;

          plotIds.forEach((plotId, idx) => {
            if (!(plotId in newAssignments)) {
              newAssignments[plotId] = nextCellIndex++;
            }
            if (!(plotId in newPositions)) {
              newPositions[plotId] = {
                x: (idx % 3) * 480 + 50,
                y: Math.floor(idx / 3) * 400 + 50,
              };
            }
            if (!(plotId in newSizes)) {
              newSizes[plotId] = { ...DEFAULT_PLOT_SIZE };
            }
          });

          set((state) => ({
            tabLayouts: {
              ...state.tabLayouts,
              [tabId]: {
                ...existingLayout,
                gridAssignments: newAssignments,
                canvasPositions: newPositions,
                plotSizes: newSizes,
              },
            },
          }));
          return;
        }

        // Initialize fresh layout
        const gridAssignments: Record<string, number> = {};
        const canvasPositions: Record<string, PlotPosition> = {};
        const plotSizes: Record<string, PlotSize> = {};

        plotIds.forEach((plotId, idx) => {
          gridAssignments[plotId] = idx;
          canvasPositions[plotId] = {
            x: (idx % 3) * 480 + 50,
            y: Math.floor(idx / 3) * 400 + 50,
          };
          plotSizes[plotId] = { ...DEFAULT_PLOT_SIZE };
        });

        set((state) => ({
          tabLayouts: {
            ...state.tabLayouts,
            [tabId]: {
              ...DEFAULT_LAYOUT,
              gridAssignments,
              canvasPositions,
              plotSizes,
            },
          },
        }));
      },

      clearTabLayout: (tabId) => {
        set((state) => {
          const { [tabId]: _, ...rest } = state.tabLayouts;
          return { tabLayouts: rest };
        });
      },
    }),
    {
      name: 'plot-view-storage',
      partialize: (state) => ({ tabLayouts: state.tabLayouts }),
    }
  )
);
